import type { RoutePermissionKey } from "./route-permission-keys";

const OFFICE_ALL_PERMISSIONS: RoutePermissionKey[] = [
  "office",
  "office:workbench",
  "office:myClass",
  "office:myStudents",
  "office:assignPractice",
  "office:assignExam",
  "office:wordPk",
  "office:wordPk:match",
  "office:wordPk:settlement",
  "office:wordReading",
  "office:wordReading:reading",
  "office:soundmark",
  "office:practiceReview",
  "office:practiceScore",
  "office:learningTracking",
  "office:learningTracking:studentDetail",
  "office:profile",
];

const withOffice = (
  permissions: RoutePermissionKey[]
): RoutePermissionKey[] => [...OFFICE_ALL_PERMISSIONS, ...permissions];

/**
 * 角色 → permissionKey 映射（静态 RBAC）
 * - Workbench：对齐老项目 role-route-permission-map
 * - office：新项目教师端；常用后台角色一并挂载，避免运维/联调进不了 Office
 * - `admin` / `ROOT` 在权限管理器中按超管直通
 */
export const ROLE_ROUTE_PERMISSION_MAP: Record<string, RoutePermissionKey[]> = {
  ROOT: withOffice([
    "Dashboard2",
    "examManagement",
    "mockExamManagement",
    "schoolManagement",
    "areaManagement",
    "test",
    "statisticsManagement",
    "scoreReportPlatform",
    "aiClassRoom",
    "system",
    "system:systemLog",
    "system:rolePermissionManager",
    "system:contractTemplate",
    "system:softwareUpgrade",
    "schoolManagement:schoolContractList:pickCurrentOnly",
    "schoolManagement:classRoomList:bindClass",
  ]),

  admin: withOffice([
    "Dashboard2",
    "examManagement",
    "mockExamManagement",
    "schoolManagement",
    "areaManagement",
    "test",
    "statisticsManagement",
    "scoreReportPlatform",
    "aiClassRoom",
    "system",
    "system:systemLog",
    "system:rolePermissionManager",
    "system:contractTemplate",
    "system:softwareUpgrade",
    "schoolManagement:schoolContractList:pickCurrentOnly",
    "schoolManagement:classRoomList:bindClass",
  ]),

  formalExamAdmin: withOffice([
    "Dashboard2",
    "Dashboard2:formalExamAdmin",
    "examManagement",
    "examManagement:examProjectList",
    "examManagement:examProjectList:add",
    "examManagement:examProjectList:edit",
    "examManagement:examProjectList:del",
    "examManagement:examProjectList:eval",
    "examManagement:examProjectList:copy",
    "examManagement:examProjectList:exportProjectpack",
    "examManagement:examStudentList",
    "examManagement:examSchoolList",
    "examManagement:examSchoolStatisticsDetails",
    "examManagement:examAutoScoreList",
    "examManagement:examStudentScoreList",
    "examManagement:newExamStudent",
    "examManagement:editExamStudent",
    "examManagement:newExamProject",
    "examManagement:editExamProject",
    "examManagement:viewExamProject",
    "statisticsManagement:examProjectReport",
    "scoreReportPlatform:scoreProject",
    "scoreReportPlatform:scoreProjectEditor",
    "schoolManagement:schoolList",
    "schoolManagement:examRoomList",
    "schoolManagement:schoolList:examRoom",
    "schoolManagement:schoolList:copyInfo",
    "schoolManagement:schoolList:register",
    "schoolManagement:schoolList:unregister",
    "schoolManagement:examRoomList:copyInfo",
    "schoolManagement:examRoomList:cancel",
  ]),

  mockExamAdmin: withOffice([
    "Dashboard2",
    "Dashboard2:mockExamAdmin",
    "mockExamManagement",
    "mockExamManagement:examStudentTemplateList",
    "mockExamManagement:mockExamStudentList",
    "mockExamManagement:examProjectList",
    "mockExamManagement:examAutoScoreList",
    "mockExamManagement:examStudentScoreList",
    "statisticsManagement:examProjectReport",
    "scoreReportPlatform:scoreProject",
    "scoreReportPlatform:scoreProjectEditor",
    "schoolManagement:schoolList",
    "schoolManagement:examRoomList",
    "schoolManagement:examRoomList:copyInfo",
    "schoolManagement:examRoomList:cancel",
    "examManagement:examProjectList",
    "examManagement:examProjectList:add",
    "examManagement:examProjectList:edit",
    "examManagement:newExamProject",
    "examManagement:editExamProject",
    "examManagement:viewExamProject",
    "aiClassRoom",
    "aiClassRoom:bookList",
    "aiClassRoom:bookList:add",
    "aiClassRoom:bookList:edit",
    "aiClassRoom:bookList:del",
    "aiClassRoom:bookDetail",
    "aiClassRoom:wordList",
    "aiClassRoom:wordList:add",
    "aiClassRoom:wordList:edit",
    "aiClassRoom:wordList:del",
    "aiClassRoom:question",
    "aiClassRoom:question:add",
    "aiClassRoom:question:edit",
    "aiClassRoom:question:del",
  ]),

  platformAdmin: withOffice([
    "Dashboard2",
    "Dashboard2:platformAdmin",
    "schoolManagement",
    "schoolManagement:schoolList",
    "schoolManagement:schoolList:export",
    "schoolManagement:schoolList:addContract",
    "schoolManagement:schoolList:add",
    "schoolManagement:schoolList:select",
    "schoolManagement:schoolList:edit",
    "schoolManagement:createSchool",
    "schoolManagement:schoolContractList",
    "schoolManagement:schoolContractList:contract",
    "schoolManagement:schoolContractList:edit",
    "schoolManagement:examRoomList",
    "schoolManagement:examRoomList:cancel",
    "schoolManagement:examRoomList:add",
    "schoolManagement:examRoomList:edit",
    "schoolManagement:examRoomList:delete",
    "schoolManagement:examRoomList:upgradeSetting",
    "schoolManagement:newExamRoom",
    "schoolManagement:editExamRoom",
    "schoolManagement:classRoomList",
    "schoolManagement:classRoomList:add",
    "schoolManagement:classRoomList:edit",
    "schoolManagement:classRoomList:bindClass",
    "schoolManagement:newClassRoom",
    "schoolManagement:editClassRoom",
    "schoolManagement:classList",
    "schoolManagement:studentList",
    "areaManagement",
    "areaManagement:areaList",
    "test:testPaperPackageList:useAuth",
    "test:testList:add",
    "test:testList:useAuthCount",
    "test:testList:select",
    "test:testList:edit",
    "test:testList:del",
    "test:testList:useAuth",
    "test:schoolTestPaperUseList:useAuthCount",
    "test:schoolTestPaperUseList:select",
    "test:schoolTestPaperUseList:edit",
    "system:systemUserEditor",
    "system:teacherList",
    "system:teacherList:resetPassword",
    "system:teacherList:add",
    "system:teacherList:edit",
    "system:teacherList:setInfo",
    "system:teacherList:delete",
    "system:teacherList:userSchoolAuth",
    "system:userSchoolAuth",
    "system:userSchoolAuthAll",
    "system:softwareUpgrade",
  ]),

  contentAdmin: withOffice([
    "Dashboard2",
    "Dashboard2:contentAdmin",
    "test",
    "test:testPaperPackageList",
    "test:testPaperPackageList:useAuth",
    "test:testList",
    "test:testList:add",
    "test:testList:useAuthCount",
    "test:testList:select",
    "test:testList:edit",
    "test:testList:importTestAnswer",
    "test:testList:doTestAnswer",
    "test:testList:del",
    "test:testList:useAuth",
    "test:testEditor",
    "test:testAnswer",
    "test:schoolTestPaperUseList",
    "test:schoolTestPaperUseList:useAuthCount",
    "test:schoolTestPaperUseList:select",
    "test:schoolTestPaperUseList:edit",
    "test:testPaperUseAuth",
    "test:resourceTemplateList",
    "test:resourceTemplateList:add",
    "test:resourceTemplateList:edit",
    "test:resourceTemplateList:del",
    "test:resourceTemplateEditor",
    "aiClassRoom",
    "aiClassRoom:bookList",
    "aiClassRoom:bookList:add",
    "aiClassRoom:bookList:edit",
    "aiClassRoom:bookList:del",
    "aiClassRoom:bookDetail",
    "aiClassRoom:wordList",
    "aiClassRoom:wordList:add",
    "aiClassRoom:wordList:edit",
    "aiClassRoom:wordList:del",
    "aiClassRoom:question",
    "aiClassRoom:question:add",
    "aiClassRoom:question:edit",
    "aiClassRoom:question:del",
    "system:systemUserEditor",
    "system:teacherList",
    "system:teacherList:resetPassword",
    "system:teacherList:add",
    "system:teacherList:edit",
    "system:teacherList:setInfo",
    "system:teacherList:delete",
    "system:teacherList:userSchoolAuth",
    "system:userSchoolAuth",
    "system:userSchoolAuthAll",
  ]),

  teacherLeader: withOffice([
    "Dashboard2",
    "Dashboard2:teacher",
    "mockExamManagement:examStudentTemplateList",
    "mockExamManagement:examProjectList",
    "mockExamManagement:examAutoScoreList",
    "mockExamManagement:examStudentScoreList",
    "schoolManagement:schoolList",
    "schoolManagement:examRoomList",
    "test:testPaperPackageList",
    "test:testList",
    "statisticsManagement:examProjectReport",
    "scoreReportPlatform:scoreProjectEditor",
    "aiClassRoom",
    "aiClassRoom:bookList",
    "aiClassRoom:bookList:add",
    "aiClassRoom:bookList:edit",
    "aiClassRoom:bookList:del",
    "aiClassRoom:bookDetail",
    "aiClassRoom:wordList",
    "aiClassRoom:wordList:add",
    "aiClassRoom:wordList:edit",
    "aiClassRoom:wordList:del",
    "aiClassRoom:question",
    "aiClassRoom:question:add",
    "aiClassRoom:question:edit",
    "aiClassRoom:question:del",
  ]),

  teacher: withOffice([
    "Dashboard2",
    "Dashboard2:teacher",
    "mockExamManagement:examStudentTemplateList",
    "mockExamManagement:examProjectList",
    "mockExamManagement:examAutoScoreList",
    "mockExamManagement:examStudentScoreList",
    "schoolManagement:schoolList",
    "schoolManagement:examRoomList",
    "test:testPaperPackageList",
    "test:testList",
    "statisticsManagement:examProjectReport",
    "scoreReportPlatform:scoreProjectEditor",
    "aiClassRoom",
    "aiClassRoom:bookList",
    "aiClassRoom:bookList:add",
    "aiClassRoom:bookList:edit",
    "aiClassRoom:bookList:del",
    "aiClassRoom:bookDetail",
    "aiClassRoom:wordList",
    "aiClassRoom:wordList:add",
    "aiClassRoom:wordList:edit",
    "aiClassRoom:wordList:del",
    "aiClassRoom:question",
    "aiClassRoom:question:add",
    "aiClassRoom:question:edit",
    "aiClassRoom:question:del",
  ]),

  businessAdmin: withOffice([
    "Dashboard2",
    "Dashboard2:businessAdmin",
    "schoolManagement",
    "schoolManagement:schoolList",
    "schoolManagement:schoolList:recordPurchase",
    "schoolManagement:schoolContractList",
    "schoolManagement:schoolContractList:contract",
    "schoolManagement:examRoomList",
    "schoolManagement:examRoomList:copyInfo",
    "schoolManagement:classRoomList",
    "schoolManagement:classList",
    "schoolManagement:studentList",
    "schoolManagement:schoolContractList:returnedPercent",
    "schoolManagement:schoolContractList:sharedPercent",
    "schoolManagement:schoolContractList:amount",
    "mockExamManagement",
    "mockExamManagement:examStudentTemplateList",
    "mockExamManagement:examProjectList",
    "mockExamManagement:examAutoScoreList",
    "mockExamManagement:examStudentScoreList",
    "schoolManagement:schoolList:examRoom",
    "schoolManagement:examRoomList:cancel",
    "system:teacherList",
    "system:teacherList:resetPassword",
    "system:salesList",
    "system:userSchoolAuth",
    "system:userSchoolAuthAll",
  ]),

  salesman: withOffice([
    "Dashboard2",
    "Dashboard2:mockExamAdmin",
    "schoolManagement",
    "schoolManagement:schoolList",
    "schoolManagement:schoolContractList",
    "schoolManagement:schoolContractList:contract",
    "schoolManagement:examRoomList",
    "schoolManagement:examRoomList:copyInfo",
    "schoolManagement:classRoomList",
    "schoolManagement:classList",
    "schoolManagement:studentList",
    "mockExamManagement",
    "mockExamManagement:examStudentTemplateList",
    "mockExamManagement:examProjectList",
    "mockExamManagement:examAutoScoreList",
    "mockExamManagement:examStudentScoreList",
    "schoolManagement:schoolList:examRoom",
    "schoolManagement:examRoomList:cancel",
    "system:teacherList",
    "system:teacherList:resetPassword",
  ]),

  insiderman: withOffice([
    "schoolManagement",
    "schoolManagement:schoolList",
    "schoolManagement:schoolList:recordPurchase",
    "schoolManagement:schoolContractList",
    "schoolManagement:schoolContractList:contract",
  ]),

  systemAdmin: [
    "schoolManagement",
    "schoolManagement:schoolList",
    "schoolManagement:schoolList:export",
    "schoolManagement:schoolList:addContract",
    "schoolManagement:schoolList:add",
    "schoolManagement:schoolList:select",
    "schoolManagement:schoolList:edit",
    "schoolManagement:createSchool",
    "schoolManagement:schoolList:contract",
    "schoolManagement:schoolList:copyInfo",
    "schoolManagement:schoolList:register",
    "schoolManagement:schoolList:unregister",
    "schoolManagement:schoolList:recordPurchase",
  ],
};

export const ROLE_PERMISSION_OVERVIEW = {
  admin: "超级管理员 - 拥有所有路由权限，在代码中特殊处理",
  ROOT: "超级管理员 - 拥有所有路由权限，在代码中特殊处理",
  formalExamAdmin:
    "考试管理员 - 负责正式考试相关管理，包括考试项目、学生、成绩等",
  mockExamAdmin: "练习管理员 - 负责模拟考试/练习相关管理，包括练习项目、模板等",
  platformAdmin: "平台管理员 - 负责学校、考场、教室等平台基础管理",
  contentAdmin: "内容管理员 - 负责资源内容管理，但不能执行测试答案相关操作",
  teacherLeader: "年级主任 - 负责年级管理，包括年级学生、教师、考试等",
  teacher: "教师 - 基础查看和练习管理权限，主要管理AI课堂内容",
  businessAdmin: "商务管理员 - 负责商务管理，包括学校、考场、教室等商务管理",
  salesman: "业务员 - 负责商务跟进，查看学校、考场、教室及合同回款等",
  insiderman: "技术支持 - 内部人员，学校相关和合约购买的权限",
  systemAdmin:
    "系统管理员 - 仅学校列表，不可删除学校/合约，新增合约限非标准/旗舰版且7天有效",
} as const;

export const SUPER_ROLE_KEYS = ["ROOT", "admin"] as const;
